/**
 * f12.2: apply a pushed projection to the cached one (design D6).
 *
 * The push REPLACES the projection wholesale, keyed by row identity -- review
 * rows by `(business_unit_id, client.id)`, grid lines by `id` -- so rows the
 * push no longer carries disappear and nothing is ever appended: a resync can
 * never duplicate a row.
 *
 * Version guard (BR-F12.2-6): when the cached row is at a HIGHER version than
 * the pushed one, the viewer holds a newer write the push was read before, so
 * its editable values and version are kept. Regressing it would turn the
 * viewer's next edit into a false `409`.
 */

import type { ForecastGrid, ForecastLine, Review, ReviewRow } from '@/api/client';

const reviewKey = (row: ReviewRow) => `${row.business_unit_id}|${row.client.id}`;

function newer(cached: number | null | undefined, pushed: number | null | undefined): boolean {
  return (cached ?? 0) > (pushed ?? 0);
}

function keepReviewAttributes(pushed: ReviewRow, cached: ReviewRow): ReviewRow {
  return {
    ...pushed,
    risk_rating: cached.risk_rating,
    upside: cached.upside,
    downside: cached.downside,
    comment: cached.comment,
    version: cached.version,
  };
}

export function mergeReview(cached: Review | undefined, pushed: Review): Review {
  if (!cached) return pushed;
  const held = new Map<string, ReviewRow>();
  for (const unit of cached.units) for (const row of unit.rows) held.set(reviewKey(row), row);
  return {
    ...pushed,
    units: pushed.units.map((unit) => ({
      ...unit,
      rows: unit.rows.map((row) => {
        const mine = held.get(reviewKey(row));
        return mine && newer(mine.version, row.version) ? keepReviewAttributes(row, mine) : row;
      }),
    })),
  };
}

export function mergeGrid(cached: ForecastGrid | undefined, pushed: ForecastGrid): ForecastGrid {
  if (!cached) return pushed;
  const held = new Map<string, ForecastLine>(cached.lines.map((line) => [line.id, line]));
  return {
    ...pushed,
    lines: pushed.lines.map((line) => {
      const mine = held.get(line.id);
      return mine && newer(mine.version, line.version) ? mine : line;
    }),
  };
}
